<template>
	<div class="container pt-6 pb-10">
		<div class="flex justify-between items-center py-2 mb-4">
			<h1 class="page-title">Showroom-uri</h1>
		</div>
		<div>
			<v-toolbar flat color="light-grey" class="rounded mb-8">
				<div class="flex flex-row justify-between items-center">
					<div class="w-1/2 flex flex-row justify-start items-center flex-grow-0 gap-2">
						<h2 class="text-2xl font-medium">Programari {{ selectedView === 'current' ? 'curente' :
							'finalizate' }}</h2>
					</div>
					<div>
						<v-select v-model="selectedView" class="w-60" :items="views" item-text="text" item-value="value"
							label="Vizualizare" solo hide-details></v-select>
					</div>
				</div>
			</v-toolbar>

			<v-progress-linear v-if="loadingAppointments" indeterminate color="teal darken-1"
				class="mb-4"></v-progress-linear>

			<div v-else>
				<div v-if="groupedAppointments.length > 0">
					<div v-for="group in groupedAppointments" :key="group.date" class="mb-6">
						<h3 class="ml-2 mb-2">{{ formatDisplayDate(group.date) }}</h3>
						<div v-for="appointment in group.appointments" :key="appointment.id"
							class="flex flex-col md:flex-row items-center gap-10 border border-gray-300 rounded-lg p-4 mb-2"
							:class="`border-${getAppointmentColor(appointment.status)}-500 bg-${getAppointmentColor(appointment.status, 'bg')}`">
							<span class="w-32 text-lg">{{ formatDisplayTimeslot(appointment.slot_start_time,
								appointment.slot_end_time) }}</span>
							<span class="w-24 font-semibold"
								:class="`text-${getAppointmentColor(appointment.status)}`">{{
									formatStatus(appointment.status) }}</span>
							<div class="w-32 flex items-center flex-grow-0 gap-2">
								<span class="text-xl">
									{{ displayFlag(appointment.showrooms.country_code) }}
								</span>
								<span>{{ appointment.showrooms.city }}</span>
							</div>
							<Called :item="appointment" :user="user" @openModal="openCalledModal" />
							<span class="w-1/5">{{ appointment.customer_name }}</span>
							<span class="w-1/6">{{ appointment.customer_email }}</span>
							<span class="w-1/6">{{ appointment.customer_phone }}</span>
						</div>
					</div>
				</div>
				<div v-else class="text-center py-10">
					Nu exista programari {{ selectedView === 'current' ? 'curente' : 'finalizate' }}.
				</div>
			</div>

			<v-dialog v-model="visibleCalledModal" max-width="340">
				<v-card class="p-4">
					<v-form ref="calledForm" v-model="validCalledForm" @submit.prevent="changeCalledStatus">
						<h2 class="text-lg font-medium mb-4">
							{{ hasNote ? 'Schimba status sunat' : 'Adauga status sunat' }}
						</h2>

						<p v-if="hasNote && !editCalledStatus" class="mb-4">
							<span v-if="calledStatusItem.called_user" class="font-bold">
								{{ calledStatusItem.called_user.replace(/ .*/, '') }}:
							</span>
							{{ calledStatusItem.note_text }}
						</p>

						<v-textarea v-else outlined label="Nota" v-model="calledStatusItem.note_text"
							:rules="[rules.required]" />

						<div class="flex flex-row justify-end gap-2 mt-4">
							<template v-if="hasNote && !editCalledStatus">
								<v-btn color="teal" dark class="uppercase" large
									@click="editCalledStatus = true">Editeaza</v-btn>
							</template>
							<template v-else>
								<v-btn color="blue-grey" class="uppercase" dark large
									@click="closeCalledModal">Anuleaza</v-btn>
								<v-btn color="teal" dark class="uppercase" large type="submit">Salveaza</v-btn>
							</template>
						</div>
					</v-form>
				</v-card>
			</v-dialog>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import flags from "@/assets/countries-flags.json"
import Called from './Called.vue';


export default {
	name: "Showroom",
	components: {
		Called
	},
	data() {
		return {
			views: [
				{ text: 'Curente', value: 'current' },
				{ text: 'Finalizate', value: 'history' }
			],
			visibleCalledModal: false,
			validCalledForm: false,
			calledStatusItem: {},
			editCalledStatus: false,
			hasNote: false,
			rules: {
				required: value => !!value || 'Câmp obligatoriu',
			}
		};
	},
	computed: {
		...mapState('showrooms', {
			appointments: state => state.appointments,
			loadingAppointments: state => state.loadingAppointments,
		}),
		user() {
			return this.$store.getters.loggedUser;
		},
		selectedView: {
			get() {
				return this.$store.state.showrooms.currentView || 'current';
			},
			set(value) {
				this.$store.commit('showrooms/setCurrentView', value);
				this.fetchShowroomAppointments({ view: value });
			}
		},
		groupedAppointments() {
			const grouped = this.appointments.reduce((acc, appointment) => {
				const date = appointment.appointment_date;
				if (!acc[date]) {
					acc[date] = [];
				}
				acc[date].push(appointment);
				return acc;
			}, {});

			// need to sort inverse for history view and normal for current view

			const sortedDates = Object.keys(grouped).sort((a, b) => {
				if (this.selectedView === 'history') {
					return new Date(b) - new Date(a); // descending for history
				} else {
					return new Date(a) - new Date(b); // ascending for current
				}
			});
			return sortedDates.map(date => ({
				date,
				appointments: grouped[date]
			}));
		}
	},
	watch: {

	},
	methods: {
		...mapActions('showrooms', ['fetchShowroomAppointments', 'updateAppointmentCalledStatus']),
		formatDisplayDate(dateString) {
			// Optional: format for display (e.g., "12 January 2026")
			const date = new Date(dateString);
			return date.toLocaleDateString('ro-RO', {
				day: 'numeric',
				month: 'long',
				year: 'numeric'
			});
		},
		formatDisplayTimeslot(startTime, endTime) {
			// Optional: format for display (e.g., "14:00 - 15:00")
			const formatTime = (timeString) => {
				const [hours, minutes] = timeString.split(':');
				return `${hours}:${minutes}`;
			};
			return `${formatTime(startTime)} - ${formatTime(endTime)}`;
		},
		formatStatus(status) {
			switch (status) {
				case 'confirmed':
					return 'confirmata';
				case 'cancelled':
					return 'anulata';
				case 'completed':
					return 'finalizata';
				default:
					return '';
			}
		},
		getAppointmentColor(status, selector) {
			switch (status) {
				case 'confirmed':
					return selector === 'bg' ? 'white' : 'green-500';
				case 'cancelled':
					return selector === 'bg' ? 'red-50' : 'red-500';
				default:
					return selector === 'bg' ? 'white' : 'grey-500';
			}

		},
		displayFlag(country) {
			const flagCountry = flags[country]
			return flagCountry.emoji
		},
		openCalledModal(item) {
			this.calledStatusItem = Object.assign({}, { supabase_appt_id: item.id, called: item.note?.called, called_user_id: item.note?.called_user_id, called_user: item.note?.called_user, note_text: item.note?.note_text });
			this.hasNote = !!item.note?.note_text;
			this.visibleCalledModal = true;
		},
		closeCalledModal() {
			this.visibleCalledModal = false;
			this.$nextTick(() => {
				this.calledStatusItem = {};
				this.editCalledStatus = false;
				this.hasNote = false;
			});
		},
		changeCalledStatus() {
			const valid = this.$refs.calledForm.validate();

			if (!valid) return;

			const newCalledStatus = {
				supabase_appt_id: this.calledStatusItem.supabase_appt_id,
				called: this.calledStatusItem.called || 1,
				called_user_id: this.calledStatusItem.called_user_id || this.user.id_user,
				called_user: this.calledStatusItem.called_user || this.user.username,
				note_text: this.calledStatusItem.note_text
			}
			this.updateAppointmentCalledStatus(newCalledStatus);
			this.closeCalledModal();
		}
	},
	mounted() {
		this.selectedView = 'current';
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h3 {
	@apply text-xl font-semibold mt-4 mb-2;
}
</style>
